import { useEffect, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import {
  SOURCE_LABEL,
  amazonUrl,
  byMerchant,
  formatPerComparableUnit,
  trend,
  validPoints,
  vencidos,
} from '@domain/product'
import type { Merchant, PricePoint, Product, ProductFamily } from '@domain/product'
import { formatBRL } from '@shared/money/format'
import { dayAndMonth } from '@shared/date/civil'
import { urlDoGoogle, urlDoNotaParana } from '@ui/produtos/busca-externa'
import { TambemConhecidoComo } from '@ui/produtos/TambemConhecidoComo'
import { Glass } from '@ui/glass/Glass'
import { Voltar } from '@ui/shell/Voltar'
import { Numero as NumeroHeroi } from '@ui/glass/Numero'
import type { Category } from '@domain/finance'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Familia } from '@ui/produtos/Familia'
import { SeletorDeFamilia } from '@ui/produtos/SeletorDeFamilia'
import { RegistrarPreco } from '@ui/produtos/RegistrarPreco'
import { PontoDePreco } from '@ui/produtos/PontoDePreco'
import { MenuFlutuante } from '@ui/shell/MenuFlutuante'
import { Dobra } from '@ui/shell/Dobra'
import { FotoAmpliada } from '@ui/produtos/FotoAmpliada'
import { Miniatura } from '@ui/produtos/Miniatura'
import { FolhaDeProduto } from '@ui/produtos/FolhaDeProduto'
import { ConfirmarApagar } from '@ui/produtos/ConfirmarApagar'
import { BotaoLembrar } from '@ui/lists/BotaoLembrar'

/**
 * O detalhe de um produto: o que se pagou, a tendência, e onde é mais barato.
 * `Docs/05-Plano/Fase 10 - Inteligência de preços.md` — a metade que depende só
 * de dado próprio.
 *
 * ## Toda comparação é por UNIDADE BASE
 *
 * Primeira linha da definição de pronto da fase, e a pergunta que justifica o
 * projeto: *"500ml a R$ 3,99 é corretamente apontado como mais caro que 1L a
 * R$ 6,99"*. O preço da embalagem aparece porque é o que a pessoa lembra de ter
 * pagado; **quem decide é a coluna por unidade base**, e ela vem em milicentavos
 * com cinco casas — em duas, os dois preços do exemplo viram "R$ 0,01".
 *
 * ## A ORIGEM de cada preço é visível, sempre
 *
 * *"'observado em 12/08, Nota Paraná'"* — segunda linha da definição de pronto.
 * Cada ponto diz se foi **pago** ou **visto**, porque um preço visto na gôndola
 * e um preço pago são coisas diferentes e a média só usa o segundo. Sem o
 * rótulo, a lista pareceria um extrato de compras que não é.
 *
 * ## Monitorar NÃO liga nem desliga o alerta de queda
 *
 * O ★ marca o que a pessoa quer acompanhar, e é isso que a seção de Produtos
 * mostra primeiro. Amarrar `product.price_drop` a ele foi considerado e
 * recusado: **a queda que mais vale saber é a de um produto que ninguém pensou
 * em marcar.** O alerta já nasce in-app justamente para poder valer para todos
 * sem incomodar ninguém.
 *
 * ## Sem base, a tela NÃO afirma
 *
 * *"O app é honesto sobre não saber: sem dado suficiente, não inventa média."* A
 * tendência some quando há menos de quatro compras — e some de verdade, em vez
 * de aparecer como "estável", que é uma afirmação sobre um produto comprado
 * duas vezes.
 */

export function Produto() {
  const { id } = useParams({ from: '/protegidas/produtos/$id' })
  const { productRepository, categoryRepository } = useFinanceRepositories()

  const [produto, setProduto] = useState<Product | null | 'carregando'>('carregando')
  const [pontos, setPontos] = useState<PricePoint[]>([])
  const [categorias, setCategorias] = useState<Category[]>([])
  const [cadastroDeLojas, setCadastroDeLojas] = useState<Merchant[]>([])
  const [familias, setFamilias] = useState<ProductFamily[]>([])
  const [registrando, setRegistrando] = useState(false)
  const [vendoFoto, setVendoFoto] = useState(false)
  const [editando, setEditando] = useState(false)
  const [apagando, setApagando] = useState(false)
  /** Sobe a cada gravação, para o efeito reler o histórico. */
  const [versao, setVersao] = useState(0)

  useEffect(() => {
    // Sair da tela enquanto a leitura está no ar é o caminho comum, não a
    // exceção — mesma guarda de `useTransaction`.
    let live = true

    void Promise.all([
      productRepository.byId(id),
      productRepository.priceHistory(id),
      categoryRepository.list(),
      productRepository.listMerchants(),
      productRepository.listFamilies(),
    ])
      .then(([p, h, cats, merchants, fams]) => {
        if (!live) return
        setProduto(p)
        setPontos(h)
        setCategorias(cats)
        setCadastroDeLojas(merchants)
        setFamilias(fams)
      })
      .catch((erro: unknown) => {
        // Falha vira "não encontrado" na tela **e** fica no console. Engolir
        // produziria um produto vazio que a tela leria como "sem histórico".
        console.error('Produto: a leitura falhou', erro)
        if (live) setProduto(null)
      })

    return () => {
      live = false
    }
  }, [productRepository, categoryRepository, id, versao])

  if (produto === 'carregando') {
    return <p className="text-sm text-[var(--text-muted)]">Carregando…</p>
  }
  if (produto === null) {
    return (
      <section>
        <Voltar para="/produtos">Produtos</Voltar>
        <p className="text-sm">Produto não encontrado.</p>
      </section>
    )
  }

  /**
   * Troca a família. Otimista, e **sem reler**.
   *
   * Otimista porque é um vínculo: o pior caso de falha é o seletor voltar, e
   * não um número de dinheiro errado. Sem reler porque a releitura custaria
   * uma volta ao servidor para saber o que já se sabe — e, no intervalo,
   * devolveria o valor ANTIGO por um render, desfazendo na cara de quem
   * acabou de escolher. A régua lá embaixo depende de `produto.familyId`, que
   * já mudou aqui.
   */
  const trocarFamilia = (familyId: string | null) => {
    const anterior = produto
    setProduto({ ...produto, familyId })
    void productRepository.update(produto.id, { familyId }).catch((erro: unknown) => {
      console.error('Produto: não deu para trocar a família', erro)
      setProduto(anterior)
    })
  }

  const validos = validPoints(pontos)
  // Os anulados, para a lista de baixo. Complemento de `validos` restrito ao
  // que foi anulado: um ponto **corrigido** também está fora de `validos`, e
  // ele já se explica na própria linha do substituto.
  const anulados = pontos.filter((p) => p.voidedAt !== null)
  const tendencia = trend(pontos)
  const lojas = byMerchant(pontos)
  // Quais linhas do histórico a própria loja já desmentiu — elas ficam, riscadas.
  const foraDeValidade = vencidos(pontos)
  const base = produto.stats.baseUnit
  /**
   * O preço por unidade base de HOJE — o último visto, não a média.
   *
   * É o número que responde "compensa?" na gôndola. A média e o menor contam a
   * história; este conta o presente, e por isso é ele que vira herói.
   *
   * **Vem junto com a ORIGEM, e isso não é enfeite.** Um preço *pago* e um
   * preço *visto na gôndola* são coisas diferentes: a média só usa o primeiro.
   * Sem o rótulo, a tela mostrava "R$ 9,45 / L" em cima de "nenhuma compra
   * registrada" — duas afirmações verdadeiras que se leem como contradição.
   */
  const ultimo =
    validos.length === 0
      ? null
      : [...validos].sort((a, b) => a.observedAt.localeCompare(b.observedAt))[validos.length - 1]!
  const heroi =
    base === null || ultimo === null
      ? null
      : {
          valor: formatPerComparableUnit(ultimo.pricePerBaseUnitMilliCents, base),
          /**
           * O preço da EMBALAGEM, ao lado do preço por unidade.
           *
           * O por-unidade responde "compensa?", e é por isso que é o herói.
           * Mas ninguém paga R$ 37,80 por um café: paga R$ 18,90 pelo pacote,
           * e é esse o número que se confere contra a etiqueta da gôndola.
           * Mostrar só o derivado obrigava a conta de cabeça na hora de checar.
           */
          daEmbalagem: formatBRL(ultimo.priceCents),
          origem: SOURCE_LABEL[ultimo.source],
          quando: ultimo.observedAt,
        }

  return (
    <section>
      {/* **A linha de navegação: sair à esquerda, o resto à direita.**

          O "⋯" morava dentro do cartão, e ali competia com a identidade do
          produto — foto, nome, preço. Ele não é sobre ESTE produto, é sobre o
          que dá para fazer a partir desta tela, igual ao Voltar. Juntos numa
          linha própria, o cartão volta a ser só o produto.

          Some junto o `absolute` que o tirava do fluxo lá dentro: aqui ele tem
          coluna própria e não rouba largura de ninguém. */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <Voltar para="/produtos">Produtos</Voltar>
        {/* Menu FLUTUANTE, e não gaveta.

            A primeira versão abria os três links em linha cheia abaixo do
            cartão: o conteúdo empurrava a página e nada dizia que aquilo
            pertencia ao "⋯". Ver `MenuFlutuante`. */}
        <MenuFlutuante rotulo="Mais ações" gatilho="⋯">
          {/* **O único item que fica DENTRO do app, e por isso vem primeiro.**

              Os outros três perguntam a estranhos quanto custa em algum lugar;
              este leva à comparação entre os tamanhos que a casa já cadastrou,
              que é dado próprio. Sem família o item não aparece — um item que
              leva a lugar nenhum é pior do que item nenhum, a mesma regra do
              Nota Paraná sem código de barras.

              É um `<Link>`, e não um `<a href>`: navegação interna que
              recarregasse o app perderia o histórico, e é dele que o "Voltar"
              desta tela depende (decisão 243). */}
          {produto.familyId !== null && (
            <>
              <Link
                to="/grupos/$id"
                params={{ id: produto.familyId }}
                className="alvo justify-start rounded-lg px-3 text-left text-sm"
              >
                Ver o grupo
              </Link>
              <span aria-hidden="true" className="my-1 block h-px bg-[var(--border)]" />
            </>
          )}

          {/* Sempre disponível: procurar o produto não depende de haver
              preço, código, ou qualquer coisa registrada. */}
          <a
            href={urlDoGoogle(produto)}
            target="_blank"
            rel="noreferrer noopener"
            className="alvo justify-start rounded-lg px-3 text-left text-sm"
          >
            Buscar no Google
          </a>

          {/* O preço do MESMO código de barras nas notas emitidas perto
              daqui — é a única das três que responde "quanto custa aqui" em
              vez de "quanto custa em algum lugar". Por isso leva a cor da
              marca. Sem código de barras não há o que perguntar. */}
          {produto.barcode !== null && (
            <a
              href={urlDoNotaParana(produto.barcode)}
              target="_blank"
              rel="noreferrer noopener"
              className="alvo justify-start rounded-lg px-3 text-left text-sm text-[var(--brand)]"
            >
              Ver no Nota Paraná
            </a>
          )}

          {/* Vai para a PÁGINA do produto (`/dp/<asin>`), não para uma busca —
              o ASIN identifica a página exata, e `amazonUrl` já existia para o
              campo da ficha. Sem ASIN o item não aparece: um item que leva a
              uma busca vazia é pior do que item nenhum. */}
          {produto.asin !== null && (
            <a
              href={amazonUrl(produto.asin)}
              target="_blank"
              rel="noreferrer noopener"
              className="alvo justify-start rounded-lg px-3 text-left text-sm"
            >
              Ver na Amazon
            </a>
          )}

          {/* Separado por borda e em vermelho: é o único item que TIRA algo do
              app; os outros quatro só levam a algum lugar. */}
          <span aria-hidden="true" className="my-1 block h-px bg-[var(--border)]" />
          <button
            type="button"
            onClick={() => {
              setApagando(true)
            }}
            className="alvo justify-start rounded-lg px-3 text-left text-sm text-[var(--negative)]"
          >
            Apagar este produto
          </button>
        </MenuFlutuante>
      </div>

      {/*
        O herói: a foto, o nome e o **preço por unidade base**.

        Antes o número por unidade morava dentro de uma grade de três, do mesmo
        tamanho de "último pago" e "média" — e ele é a pergunta que justifica o
        projeto inteiro (`Fase 10`: *"500ml a R$ 3,99 é corretamente apontado
        como mais caro que 1L a R$ 6,99"*). Um número que decide a compra não
        pode ter o mesmo peso de dois que a descrevem.

        `sobreVidro` liga a plaquinha: o algarismo fica sobre `frost`, e sem a
        placa a leitura passa a depender do que estiver passando atrás.
      */}
      {/* **Duas linhas, e não uma** (decisão 240).

          Antes era uma linha só: foto à esquerda e, à direita, o nome com os
          controles empilhados debaixo dele. A foto ficava centrada no meio
          vertical do cartão, e "Monitorar", "Editar" e o "⋯" acabavam na
          mesma altura do preço — três alvos disputando a linha que existe
          para dizer quanto custa.

          Agora a de cima é só IDENTIDADE (foto, nome, marca, preço) e a de
          baixo é só AÇÃO. `items-start` é o que sobe a foto: com `center` ela
          descia junto com o texto que cresce ao lado. */}
      <Glass level="frost" className="mb-4 flex flex-col gap-3 p-4" as="header">
        <div className="flex items-start gap-4">
          {/* Só a imagem: trocar a foto é raro e mora na ficha, lá embaixo.
            `alt` vazio porque o nome do produto está do lado — um leitor de
            tela que anunciasse "foto de Coca-Cola 2L" antes de "Coca-Cola 2L"
            diria a mesma coisa duas vezes. */}
          {/* A foto abre em tamanho grande — um `<button>`, e não a `<img>` com
            `onClick`: imagem clicável não recebe foco nem responde a Enter, e
            aqui o gesto é uma ação de verdade.

            O `alt` continua vazio: o nome do produto está do lado, e quem
            anuncia esta ação é o `aria-label` do botão. A foto se descreve na
            tela ampliada, onde ela É o conteúdo. */}
          {/* **Com foto é botão; sem foto é só o lugar dela.** Um "Ver a foto
            de X" que abre um visualizador vazio seria um alvo que mente. Mas o
            lugar fica reservado nos dois casos: antes, o herói de um produto
            sem foto tinha um layout e o de um produto com foto tinha outro — e
            quase nenhum produto desta base tem imagem. */}
          {produto.imageUrl === null ? (
            <Miniatura nome={produto.name} src={null} className="size-16" />
          ) : (
            <button
              type="button"
              onClick={() => {
                setVendoFoto(true)
              }}
              aria-label={`Ver a foto de ${produto.name}`}
              className="shrink-0"
            >
              <Miniatura
                nome={produto.name}
                src={produto.imageUrl}
                ajuste="conter"
                className="size-16"
              />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold leading-tight tracking-tight">{produto.name}</h1>
            {produto.brand !== null && (
              <p className="text-xs text-[var(--text-muted)]">{produto.brand}</p>
            )}
            {heroi === null ? (
              <p className="mt-2 text-sm text-[var(--text-muted)]">
                {/*
                A falta é do PRODUTO, e não de `stats.baseUnit` — que é derivado
                dos pontos de preço e nasce nulo enquanto não há nenhum. Testar
                `base` aqui acusaria "sem tamanho declarado" num produto com
                tamanho cadastrado e nenhum preço, que é o estado de todo
                produto recém-criado. O teste pegou.
              */}
                {produto.size === null || produto.unit === null
                  ? 'Sem tamanho declarado — não dá para comparar.'
                  : 'Sem preço ainda.'}
              </p>
            ) : (
              <>
                <p className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
                  <NumeroHeroi tom="economia" tamanho="medio">
                    {heroi.valor}
                  </NumeroHeroi>
                  {/* O preço da embalagem, menor e mais apagado que o por-unidade:
                  ele é o que se confere contra a etiqueta, não o que decide
                  entre dois tamanhos. */}
                  <span className="text-sm tabular-nums text-[var(--text-muted)]">
                    {heroi.daEmbalagem}
                  </span>
                </p>
                {/* De onde veio, e quando. É o que impede o herói de contradizer
                o "nenhuma compra registrada" logo abaixo. */}
                <p className="text-xs text-[var(--text-muted)]">
                  {`${heroi.origem} em ${dayAndMonth(heroi.quando)}`}
                </p>
              </>
            )}
          </div>
        </div>

        {/* **A linha das ações.** Monitorar e Editar mudam o produto; o
            seletor de família muda a que ele PERTENCE — os três são decisões
            sobre esta embalagem, e agora ficam juntos, abaixo da identidade em
            vez de ao lado dela. O `flex-wrap` é o que mantém isso honesto num
            celular de 375px: com uma família de nome comprido, o seletor cai
            para a linha seguinte em vez de espremer os botões abaixo dos 44px
            de alvo. */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={produto.isMonitored}
            className="rounded-full border border-[var(--border)] alvo px-3 py-1 text-xs"
            onClick={() => {
              const novo = !produto.isMonitored
              // Otimista: a tela reflete a escolha na hora, e a leitura seguinte
              // confirma. É um booleano de preferência — o pior caso de falha é
              // o ★ voltar, e não um número de dinheiro errado.
              setProduto({ ...produto, isMonitored: novo })
              void productRepository
                .update(produto.id, { isMonitored: novo })
                .catch((erro: unknown) => {
                  console.error('Produto: não deu para mudar o monitoramento', erro)
                  setProduto({ ...produto, isMonitored: !novo })
                })
            }}
          >
            {produto.isMonitored ? '★ Monitorado' : '☆ Monitorar'}
          </button>

          {/* **A ficha virou este botão** (decisão 239).

                Ela era uma dobra no fim da página com foto, códigos e um link
                "Editar este produto" que abria um SEGUNDO formulário — pior
                que o do cadastro, sem foto, sem câmera e sem busca pelo
                código. Identidade de produto é o que se conserta, não o que se
                consulta: aqui em cima é onde a decisão de corrigir nasce, e o
                que ela abre é a mesma folha que cadastra. */}
          <button
            type="button"
            className="rounded-full border border-[var(--border)] alvo px-3 py-1 text-xs"
            onClick={() => {
              setEditando(true)
            }}
          >
            Editar
          </button>

          {/* Pôr na lista de compras a partir daqui: a decisão de comprar de
              novo nasce olhando o produto — o preço que subiu, o tempo desde a
              última compra —, e não digitando o nome de memória em outra tela. */}
          <BotaoLembrar texto={produto.name} productId={produto.id} />

          {/* O vínculo com a família saiu da dobra "Tamanhos" e subiu para cá:
              escolher é a DECISÃO, e a régua que compara os tamanhos é o
              resultado dela. Ver `SeletorDeFamilia`. */}
          <SeletorDeFamilia
            produto={produto}
            familias={familias}
            onTrocarFamilia={trocarFamilia}
            onCriouFamilia={() => {
              // A lista de famílias mudou, e só ela — releitura da página, que
              // é quem a carrega.
              setVersao((v) => v + 1)
            }}
          />
        </div>
      </Glass>

      {lojas.length > 1 && (
        <Glass level="frost" className="mb-4 p-4" as="section" aria-label="Onde é mais barato">
          <h2 className="mb-2 text-sm font-medium">Onde é mais barato</h2>
          <ul>
            {lojas.map((loja) => (
              <li
                key={loja.merchantId ?? 'sem-loja'}
                className="flex items-baseline justify-between gap-2 border-b border-[var(--border)] py-1 text-sm"
              >
                {/* O NOME, e não o id — que é o que estava aparecendo aqui
                    enquanto `merchants` não existia como entidade.
                    "Sem loja" aparece em vez de sumir: parte dos pontos vem de
                    lançamento sem lugar, e escondê-los faria a comparação
                    descrever um pedaço do histórico parecendo descrever o
                    todo. */}
                <span>{nomeDaLoja(loja.merchantId, cadastroDeLojas)}</span>
                <span className="tabular-nums text-[var(--text-muted)]">
                  {`${String(loja.count)}×`}
                </span>
                <span className="tabular-nums">
                  {base === null ? '—' : formatPerComparableUnit(loja.current, base)}
                </span>
              </li>
            ))}
          </ul>
        </Glass>
      )}

      {registrando ? (
        <Glass level="frost" className="mb-4 p-4" as="section" aria-label="Registrar preço">
          <RegistrarPreco
            produto={produto}
            lojas={cadastroDeLojas}
            onGravado={() => {
              setRegistrando(false)
              // Relê: o ponto novo muda o histórico, a tendência e a comparação
              // de tamanhos, e todos os três estão nesta tela.
              setVersao((v) => v + 1)
            }}
            onCancelar={() => {
              setRegistrando(false)
            }}
          />
        </Glass>
      ) : (
        <button
          type="button"
          // Botão de MARCA, e não contorno cinza: é a ação frequente da tela —
          // o gesto do corredor do supermercado — e estava com o mesmo peso da
          // ficha, que se abre uma vez por ano.
          className="mb-4 w-full rounded-[var(--radius-md)] bg-[var(--brand)] px-4 py-2.5
                     text-sm font-semibold text-white"
          onClick={() => {
            setRegistrando(true)
          }}
        >
          Registrar um preço visto
        </button>
      )}

      {vendoFoto && produto.imageUrl !== null && (
        <FotoAmpliada
          src={produto.imageUrl}
          nome={produto.name}
          onFechar={() => {
            setVendoFoto(false)
          }}
        />
      )}

      {/* **Desenho A: as lojas em cima, o resto em dobras (decisão 236).**

          Sete cartões de vidro do mesmo peso empilhados era o que esta tela
          tinha virado. `Dobra` explica a regra; aqui fica a aplicação dela:
          aberto o que responde *onde eu compro*, dobrado o que responde *como
          foi da última vez* e *o que é este produto*. */}
      <div className="mb-4">
        <Dobra titulo="Histórico de preço">
          {produto.stats.purchaseCount === 0 ? (
            <p className="mb-4 text-sm text-[var(--text-muted)]">
              {heroi === null
                ? 'Nenhum preço registrado ainda.'
                : 'Nenhuma compra registrada — a média conta só o que foi pago; o menor preço conta o que está valendo hoje em cada loja.'}
            </p>
          ) : (
            // Sem `Glass`: dentro de uma dobra, um cartão de vidro seria cartão
            // dentro de gaveta — e ainda gastaria um `frost` do orçamento.
            <section aria-label="Preços" className="mb-4">
              <dl className="grid grid-cols-3 gap-3 text-sm">
                <Numero rotulo="Último pago" valor={formatBRL(produto.stats.lastPriceCents ?? 0)} />
                <Numero
                  rotulo="Média"
                  valor={
                    produto.stats.avgPerBaseUnitMilliCents === null || base === null
                      ? '—'
                      : formatPerComparableUnit(produto.stats.avgPerBaseUnitMilliCents, base)
                  }
                />
                <Numero
                  rotulo="Menor encontrado"
                  valor={
                    produto.stats.minPerBaseUnitMilliCents === null || base === null
                      ? '—'
                      : formatPerComparableUnit(produto.stats.minPerBaseUnitMilliCents, base)
                  }
                />
              </dl>
              {/* Some quando não há base — ver o cabeçalho. */}
              {tendencia !== null && base !== null && (
                <p className="mt-3 text-sm">
                  {tendencia.direction === 'flat'
                    ? 'O preço está estável nas últimas compras.'
                    : `O preço vem ${tendencia.direction === 'up' ? 'subindo' : 'caindo'}: ${formatPerComparableUnit(
                        tendencia.older,
                        base,
                      )} → ${formatPerComparableUnit(tendencia.recent, base)}.`}
                </p>
              )}
            </section>
          )}

          {/* Um `<div>`, e não um `<section aria-label>`: a `Dobra` por fora já
              é a região chamada "Histórico de preço", e nomear as duas daria
              duas regiões com o mesmo nome — que é ambíguo para quem navega
              por região, e quebrou o teste que procura por ela. O `<h2>` saiu
              pelo mesmo motivo: o `<summary>` já diz o título. */}
          <div>
            {validos.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">Nenhum preço registrado.</p>
            ) : (
              <ul>
                {[...validos]
                  .sort((a, b) => b.observedAt.localeCompare(a.observedAt))
                  .map((p) => (
                    <PontoDePreco
                      key={p.id}
                      ponto={p}
                      lojas={cadastroDeLojas}
                      vencido={foraDeValidade.has(p.id)}
                      aoAnular={() => {
                        // Relê pelo mesmo motivo do registro: anular muda o
                        // histórico, a tendência e a comparação de tamanhos, e os
                        // três estão nesta tela.
                        setVersao((v) => v + 1)
                      }}
                    />
                  ))}
              </ul>
            )}

            {/*
          Os anulados não somem do app — só das contas e da lista de cima.
          Escondê-los por completo faria "some sem deixar rastro", que é
          justamente o que anular existe para não ser: quem anulou por engano
          precisa poder ver o que era, e registrar de novo.
        */}
            {anulados.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer list-none text-xs text-[var(--text-muted)] underline">
                  {anulados.length === 1
                    ? '1 preço anulado'
                    : `${String(anulados.length)} preços anulados`}
                </summary>
                <ul className="mt-1 opacity-60">
                  {[...anulados]
                    .sort((a, b) => b.observedAt.localeCompare(a.observedAt))
                    .map((p) => (
                      <PontoDePreco key={p.id} ponto={p} lojas={cadastroDeLojas} />
                    ))}
                </ul>
              </details>
            )}
          </div>
        </Dobra>

        <TambemConhecidoComo productId={produto.id} />

        {/* **"Tamanhos" sobreviveu à ficha, e não por descuido.**

            Escolher a família não é dado de identidade — é um vínculo, e o
            comentário do cadastro sempre disse por que ele não cabe lá: *"a
            família é escolhida no detalhe do produto, onde a comparação por
            unidade base está à vista"*. Numa folha de cadastro seria um
            seletor sem nada com que comparar. Aqui embaixo, a régua abre logo
            abaixo dele. */}
        <Dobra titulo="Tamanhos">
          <Familia
            produto={produto}
            nomeDaFamilia={familias.find((f) => f.id === produto.familyId)?.name ?? null}
          />
        </Dobra>
      </div>

      {apagando && (
        <ConfirmarApagar
          produto={produto}
          pontos={pontos.length}
          onFechar={() => {
            setApagando(false)
          }}
        />
      )}

      {editando && (
        <FolhaDeProduto
          produto={produto}
          categorias={categorias}
          onFechar={() => {
            setEditando(false)
          }}
          onGravado={() => {
            // Relê em vez de aplicar um patch em memória: a folha também mexe
            // na FOTO, que não vem no patch dos campos, e o `imageUrl` novo só
            // existe depois de o Storage responder.
            setVersao((v) => v + 1)
          }}
          onCategoriasMudaram={() => {
            setVersao((v) => v + 1)
          }}
        />
      )}
    </section>
  )
}

function Numero({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-xs text-[var(--text-muted)]">{rotulo}</dt>
      <dd className="tabular-nums">{valor}</dd>
    </div>
  )
}

/**
 * O nome da loja, ou o aviso de que não há. **Loja apagada cai no id**: é feio e
 * é honesto — melhor mostrar a chave do que fingir que o ponto não tem lugar.
 */
function nomeDaLoja(merchantId: string | null, lojas: Merchant[]): string {
  if (merchantId === null) return 'Sem loja registrada'
  return lojas.find((l) => l.id === merchantId)?.name ?? merchantId
}
